import React, {useContext, useEffect, useRef, useState, useMemo, useCallback} from 'react';
import {
    Alert,
    Dimensions, FlatList,
    Linking, Modal,
    Platform, ScrollView,
    StyleSheet,
    Switch,
    Text,
    TextInput,
    TouchableOpacity,
    View
} from 'react-native';
import ErrorReportingService from "../helpers/ErrorReportingService";

// Conditionally import Android-specific modules
const ToastAndroid = Platform.OS === 'android' ? require('react-native').ToastAndroid : null;
import { LinearGradient } from "expo-linear-gradient";
import Feather from 'react-native-vector-icons/Feather';
import Spinner from "react-native-loading-spinner-overlay";
import { priceFormatter } from "../helpers/numbers";
import VivaPaymentModule from "../paymentModules/VivaPaymentsModule";
import { CartContext } from "../helpers/CartContext";
import { BluetoothContext } from "../helpers/BluetoothContext";
import { OrdersContext } from "../helpers/OrdersContext";
import { handlePayment, send_storeOrder } from "../helpers/paymentHelper";
import Icon from "react-native-vector-icons/Feather";
import VatModal from "../components/VatModal";
import {TableContext} from "../helpers/TableContext";
import { hungarianRoundNumbers } from '../helpers/hungarianRoundNumbers';
import PinPad from "../components/PinPad";
import {HappyDialog} from "../helpers/crossPlatformTools/dialogs";
import {VipCardContext} from "../helpers/VipCardContext";
import {useTranslation} from "react-i18next";
import ItemComponent from "../components/ItemComponent";
import Observables from "@happy-gastro/global-observables";
import {RawBTPrintKitchen, RawBTPrintTicket} from "../helpers/rawBTDriver";
import HappyAlert from "../helpers/crossPlatformTools/HappyAlert";
import TeyaPaymentDialog from "../components/TeyaPaymentDialog";

const { width, height } = Dimensions.get('window');

const VoucherModal = ({
                          visible,
                          onClose,
                          onConfirm,
                          voucherCode,
                          onVoucherCodeChange
                      }) => {
    const {t} = useTranslation();
    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            onRequestClose={onClose}
        >
            <View style={styles.modalContainer}>
                <View style={styles.modalContent}>
                    <Text style={styles.modalTitle}>{t('voucher')}</Text>

                    <TextInput
                        style={styles.input}
                        value={voucherCode}
                        onChangeText={onVoucherCodeChange}
                        placeholder={t('Enter_voucher_code')}
                        autoCapitalize="characters"
                    />

                    <View style={styles.modalButtons}>
                        <TouchableOpacity
                            style={styles.modalButton}
                            onPress={onClose}
                        >
                            <Text>{t('cancel')}</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.modalButton}
                            onPress={onConfirm}
                        >
                            <Text>{t('confirm')}</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const PaymentScreen = ({ navigation, setIsModalVisible }) => {
    const { cartTotal = 0, setCart, cartItems = [], originalCartTotal, productsPrice, discountsForWholeCart, setCartItems, serviceFeeAmount, cartTotalWithServiceFee, alreadySentCartItems, serviceFeePercentage ,setAlreadySentCartItems} = useContext(CartContext);
    const { currentTable } = useContext(TableContext);
    const { selectedVipCard, removeVipCard, selectedEmployee, removeEmployee } = useContext(VipCardContext);
    const { sendData } = useContext(BluetoothContext);
    const [processing, setProcessing] = useState(false);
    const [needInvoice, setNeedInvoice] = useState(false);
    const [needNAV, setNeedNAV] = useState(true);
    const [paymentType, setPaymentType] = useState(false);
    const [originalAmount, setOriginalAmount] = useState(cartTotal || 0);
    const [amount, setAmount] = useState(cartTotal || 0);
    const [paidAmount, setPaidAmount] = useState(0);
    const [amountToPay, setAmountToPay] = useState((originalAmount));
    const [changeToReturn, setChangeToReturn] = useState(0);
    const [modalVisible, setModalVisible] = useState(false);
    const [vatInvoiceData, setVatInvoiceData] = useState(false);
    const [isSplitOrder, setIsSplitOrder] = useState(true);
    const [payments, setPayments] = useState([]);
    const [paymentsSum, setPaymentsSum] = useState(0);
    const {orders, } = useContext(OrdersContext);
    const [showSplitItems, setShowSplitItems] = useState(false);
    const [paymentsModal, setPaymentsModal] = useState(false);
    const [selectedItemsToPay, setSelectedItemsToPay] = useState([]);
    const { t } = useTranslation();
    const [combinedCartItems, setCombinedCartItems] = useState([...alreadySentCartItems.map(item => ({ ...item, canBeModified: false })), ...cartItems.map(item => ({ ...item, canBeModified: true })).reverse()] || []);
    const [selectedItemsSum, setSelectedItemsSum] = useState(0);
    const textInputRef = useRef(null);
    const [voucherModalVisible, setVoucherModalVisible] = useState(false);
    const [isAlertVisible, setIsAlertVisible] = useState(false);
    const [voucherCode, setVoucherCode] = useState('');

    // Memoize combinedCartItems to avoid unnecessary recalculations
    const combinedCartItemsMemo = useMemo(() => {
        return [...alreadySentCartItems.map(item => ({ ...item, canBeModified: false })), 
                ...cartItems.map(item => ({ ...item, canBeModified: true })).reverse()];
    }, [alreadySentCartItems, cartItems]);

    useEffect(() => {
        setCombinedCartItems(combinedCartItemsMemo);
    }, [combinedCartItemsMemo]);

    // Memoize selectedItemsSum calculation
    const calculatedSelectedItemsSum = useMemo(() => {
        return selectedItemsToPay?.length > 0 
            ? Math.round(selectedItemsToPay.reduce((sum, item) => sum + (item.remotePrice || item.price), 0)) 
            : 0;
    }, [selectedItemsToPay]);

    useEffect(() => {
        setSelectedItemsSum(calculatedSelectedItemsSum);
    }, [calculatedSelectedItemsSum]);


    const validateAndAddVoucher = () => {
        const voucherRegex = /^Kupon\d+$/;

        if (voucherRegex.test(voucherCode)) {
            const amount = parseInt(voucherCode.replace('Kupon', ''));

            setPayments([...payments, {
                paymentType: 'voucher',
                amount: amount
            }]);

            setVoucherModalVisible(false);
            setVoucherCode('');
        } else {
            setIsAlertVisible(true)
        }
    };

    useEffect(() => {
        if(currentTable){
            const tableOrder = orders?.find(
                (order) =>
                    !order.storno &&
                    order.status !== "cancelled" &&
                    order.status !== "closed" &&
                    order.tableId === currentTable?._id
            );
            if(tableOrder?.items) {
                setAlreadySentCartItems(tableOrder?.items)
            }
        }

    },[orders])


    const toggleModal = () => {
        setPaymentsModal(!paymentsModal);
    };
    // Memoize payments sum calculation
    const calculatedPaymentsSum = useMemo(() => {
        return payments.reduce((acc, payment) => acc + payment.amount, 0);
    }, [payments]);

    // Update paymentsSum and amount when payments change
    useEffect(() => {
        setPaymentsSum(calculatedPaymentsSum);
        setAmount(originalAmount - calculatedPaymentsSum);
    }, [calculatedPaymentsSum, originalAmount]);
    // Removed commented out debug logging

    // Memoize hungarianRoundNumbers to avoid repeated calculations with the same inputs
    const memoizedHungarianRound = useCallback((value) => {
        return hungarianRoundNumbers(value);
    }, []);

    // Memoize amount calculations based on payment type and selected items
    const amountCalculations = useMemo(() => {
        if (!currentTable) return { originalAmount: 0, amount: 0 };

        const isCashPayment = paymentType === 'Készpénz' || paymentType === 'cash';
        const hasSelectedItems = selectedItemsToPay?.length > 0;
        const serviceFee = Number(serviceFeePercentage);

        if (isCashPayment) {
            if (hasSelectedItems) {
                if (!isNaN(serviceFee)) {
                    const totalWithFee = (selectedItemsSum * serviceFee) + selectedItemsSum;
                    return {
                        originalAmount: memoizedHungarianRound(totalWithFee),
                        amount: memoizedHungarianRound(totalWithFee - paymentsSum)
                    };
                } else {
                    ErrorReportingService.error("Invalid serviceFeePercentage", { serviceFeePercentage }, "PaymentScreen");
                    return {
                        originalAmount: originalAmount, // Keep existing value
                        amount: memoizedHungarianRound(selectedItemsSum - paymentsSum)
                    };
                }
            } else {
                const roundedAmount = memoizedHungarianRound(cartTotalWithServiceFee);
                return {
                    originalAmount: roundedAmount,
                    amount: roundedAmount - paymentsSum
                };
            }
        } else {
            if (hasSelectedItems) {
                const totalWithFee = (selectedItemsSum * serviceFee) + selectedItemsSum;
                return {
                    originalAmount: Math.round(totalWithFee),
                    amount: Math.round(totalWithFee - paymentsSum)
                };
            } else {
                return {
                    originalAmount: cartTotalWithServiceFee,
                    amount: cartTotalWithServiceFee - paymentsSum
                };
            }
        }
    }, [currentTable, cartTotalWithServiceFee, selectedItemsToPay, selectedItemsSum, paymentType, serviceFeePercentage, paymentsSum, memoizedHungarianRound, originalAmount]);

    // Apply the calculated values
    useEffect(() => {
        if (currentTable) {
            setOriginalAmount(amountCalculations.originalAmount);
            setAmount(amountCalculations.amount);
        }
    }, [currentTable, amountCalculations]);

    const renderItem = ({ item }) => (
        <ItemComponent
            item={item}
            selectedItemsToPay={selectedItemsToPay}
            setSelectedItemsToPay={setSelectedItemsToPay}
            priceFormatter={priceFormatter}
            styles={styles}
        />
    );

    // Memoize translateDisplay function to prevent unnecessary re-renders
    const translateDisplay = useCallback((text) => {
        // Use the t function from the component scope instead of calling useTranslation() here
        if(text === "Készpénz") return t("cash");
        if(text === "Bankkártya") return t("credit_card");
        if(text === "Szép kártya") return t("Szép kártya");
        if(text === "Utalvány") return t("voucher");
        if(text === "VIP Számla") return t("vip_balance");
        if(text === "Személyzeti") return t("employee_balance");
        return text; // Return the original text if no translation is found
    }, [t]);
    // Memoize payment options to prevent unnecessary recalculations
    const paymentOptions = useMemo(() => {
        if (selectedVipCard) {
            return [
                { type: 'Készpénz', icon: 'dollar-sign' },
                { type: 'Bankkártya', icon: 'credit-card' },
                { type: 'Szép kártya', icon: 'credit-card' },
                { type: 'Utalvány', icon: 'tag' },
                { type: 'VIP Számla', icon: 'star' } , // Add VIP Balance payment type
            ];
        } else if (selectedEmployee) {
            return [
                { type: 'Készpénz', icon: 'dollar-sign' },
                { type: 'Bankkártya', icon: 'credit-card' },
                { type: 'Szép kártya', icon: 'credit-card' },
                { type: 'Utalvány', icon: 'tag' },
                { type: 'Személyzeti', icon: 'star' } , // Add employee Balance payment type
            ];
        } else {
            return [
                { type: 'Készpénz', icon: 'dollar-sign' },
                { type: 'Bankkártya', icon: 'credit-card' },
                { type: 'Szép kártya', icon: 'credit-card' },
                { type: 'Utalvány', icon: 'tag' },
            ];
        }
    }, [selectedVipCard, selectedEmployee]);

    // Memoize formatAmountWithSpaces function to prevent unnecessary re-renders
    const formatAmountWithSpaces = useCallback((value) => {
        if (!value) return '';
        if ((typeof value === 'number' && value < 0)) return '';

        // Round the value to 2 decimal places
        const roundedValue = Math.round(value * 100) / 100;

        // Split number into integer and decimal parts
        const [integerPart, decimalPart] = roundedValue.toString().split('.');

        // Add spaces for thousands in the integer part
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

        // Ensure the decimal part has 2 digits
        const formattedDecimal = decimalPart ? decimalPart.padEnd(2, '0') : '00';

        // Return formatted value without decimal part if both decimal digits are zero
        return formattedDecimal === '00' ? formattedInteger : `${formattedInteger}.${formattedDecimal}`;
    }, []);


    // Memoize reverseConvertPaymentType function to prevent unnecessary re-renders
    const reverseConvertPaymentType = useCallback((paymentTypeValue) => {
        if (paymentTypeValue === "cash") return "Készpénz";
        if (paymentTypeValue === "creditCard") return "Bankkártya";
        if (paymentTypeValue === "szepCreditCard") return "Szép kártya";
        if (paymentTypeValue === "voucher") return "Utalvány";
        if (paymentTypeValue === "vipBalance") return "VIP egyenleg";
        if (paymentTypeValue === "employeeBalance") return "Személyzeti";
        return `${paymentTypeValue}`;
    }, []);

    const confirmPaymentAmount = async (isTip) => {
        try {
            setProcessing(true);
            const totalPaidAmount = isSplitOrder ? paymentsSum : paidAmount;
            const amountToBePaid = originalAmount;
            const surplus = totalPaidAmount - amountToBePaid;
            const tableOrder = orders?.find(
                (order) =>
                    !order.storno &&
                    order.status !== "cancelled" &&
                    order.status !== "closed" &&
                    order.tableId === currentTable?._id
            );
            if (totalPaidAmount > amountToBePaid) {
                const tip = (paymentType === 'Bankkártya' || paymentType === 'creditCard' || paymentType === 'szepCreditCard' || paymentType === 'Szép kártya') ? surplus : hungarianRoundNumbers(surplus);

                if (isTip) {
                    //Todo: test if works as expected
                    //Todo: test with creditcard if works as expected
                    if(selectedItemsToPay?.length > 0){
                        //split
                        //Split payment with not exceeding payment
                        const filteredItemsArrayForSplitOrder = selectedItemsToPay ? alreadySentCartItems.map(item => {
                            const matchingItems = selectedItemsToPay.filter(selected => selected._id === item._id && selected.size === item.size);
                            if (matchingItems.length > 0) {
                                const totalAmountToDecrease = matchingItems.reduce((sum, selectedItem) => sum + 1, 0);
                                return {
                                    ...item,
                                    amount: Math.max((item.amount || 0) - totalAmountToDecrease, 0),
                                    quantity: Math.max((item.quantity || 0) - totalAmountToDecrease, 0)
                                };
                            }
                            return item;
                        }) : alreadySentCartItems;

                        //selectedItems payment
                        const firstPayment = await handlePayment(paymentType, paidAmount, amountToBePaid, setProcessing, navigation, cartTotal, setCart, cartItems, sendData, () => {/*onClose*/
                        }, originalCartTotal, productsPrice, discountsForWholeCart, vatInvoiceData, needInvoice, false, currentTable, serviceFeeAmount, selectedItemsToPay, undefined, payments, tip, (selectedVipCard ? selectedVipCard : selectedEmployee),);

                        ErrorReportingService.info("Sending order to save", { 
                            tableOrder: tableOrder?._id, 
                            currentTable: currentTable?._id 
                        }, "PaymentScreen");
                        //order update.
                        await send_storeOrder(cartTotal, cartItems, paymentType, paidAmount, discountsForWholeCart, vatInvoiceData, needInvoice, currentTable, serviceFeeAmount, true, filteredItemsArrayForSplitOrder ,tableOrder._id, payments,tip,originalAmount,(selectedVipCard ? selectedVipCard : selectedEmployee),undefined,selectedItemsToPay,tableOrder);
                        Observables.call("reloadOrders");
                    }else{

                        return handlePayment(paymentType, paidAmount, amountToBePaid, setProcessing, navigation, cartTotal, setCart, cartItems, sendData, () => {/*onClose*/}, originalCartTotal, productsPrice, discountsForWholeCart, vatInvoiceData, needInvoice, false, currentTable, serviceFeeAmount, alreadySentCartItems, tableOrder?._id, payments, tip, (selectedVipCard ? selectedVipCard : selectedEmployee));
                    }
                }else{
                    //Todo: test if works as expected
                    //Todo: test with creditcard if works as expected
                    //this happens when the user clicks on the "Visszajáró" button
                    if(selectedItemsToPay?.length > 0){
                        //split
                        //selectedItems payment
                        const firstPayment = await handlePayment(paymentType, amountToBePaid, amountToBePaid, setProcessing, navigation, cartTotal, setCart, cartItems, sendData, () => {/*onClose*/
                        }, originalCartTotal, productsPrice, discountsForWholeCart, vatInvoiceData, needInvoice, false, currentTable, serviceFeeAmount, selectedItemsToPay, undefined, payments, 0, (selectedVipCard ? selectedVipCard : selectedEmployee),);

                        //order update.
                        await send_storeOrder(cartTotal, cartItems, paymentType, paidAmount, discountsForWholeCart, vatInvoiceData, needInvoice, currentTable, serviceFeeAmount, true, filteredItemsArrayForSplitOrder ,tableOrder._id,payments,0,originalAmount,(selectedVipCard ? selectedVipCard : selectedEmployee),undefined,selectedItemsToPay,tableOrder);
                        Observables.call("reloadOrders");
                         }else {
                        return handlePayment(paymentType, amountToBePaid, amountToBePaid, setProcessing, navigation, cartTotal, setCart, cartItems, sendData, () => {/*onClose*/
                        }, originalCartTotal, productsPrice, discountsForWholeCart, vatInvoiceData, needInvoice, false, currentTable, serviceFeeAmount, alreadySentCartItems, tableOrder?._id, payments, 0, (selectedVipCard ? selectedVipCard : selectedEmployee));
                    }
                }
            } else {
                if(selectedItemsToPay?.length > 0){
                    //Split payment with not exceeding payment
                    const filteredItemsArrayForSplitOrder = selectedItemsToPay ? alreadySentCartItems.map(item => {
                        const matchingItems = selectedItemsToPay.filter(selected => selected._id === item._id && selected.size === item.size);
                        if (matchingItems.length > 0) {
                            const totalAmountToDecrease = matchingItems.reduce((sum, selectedItem) => sum + 1, 0);
                            return {
                                ...item,
                                amount: Math.max((item.amount || 0) - totalAmountToDecrease, 0),
                                quantity: Math.max((item.quantity || 0) - totalAmountToDecrease, 0)
                            };
                        }
                        return item;
                    }) : alreadySentCartItems;
                    //selectedItems payment

                    const firstPayment = await handlePayment(paymentType, paidAmount, amountToBePaid, setProcessing, navigation, cartTotal, setCart, cartItems, sendData, () => {/*onClose*/
                    }, originalCartTotal, productsPrice, discountsForWholeCart, vatInvoiceData, needInvoice, false, currentTable, serviceFeeAmount, selectedItemsToPay, undefined, payments, 0, (selectedVipCard ? selectedVipCard : selectedEmployee),);

                    //order update.
                    await send_storeOrder(cartTotal, cartItems, paymentType, paidAmount, discountsForWholeCart, vatInvoiceData, needInvoice, currentTable, serviceFeeAmount, true, filteredItemsArrayForSplitOrder ,tableOrder._id,payments,0,originalAmount,(selectedVipCard ? selectedVipCard : selectedEmployee),undefined,selectedItemsToPay,tableOrder);
                   Observables.call("reloadOrders");
                }else {
                    //not exceeding payment
                    ErrorReportingService.info("Processing non-exceeding payment", { paymentType }, "PaymentScreen");
                    return handlePayment(paymentType, paidAmount, amountToBePaid, setProcessing, navigation, cartTotal, setCart, cartItems, sendData, () => {/*onClose*/
                    }, originalCartTotal, productsPrice, discountsForWholeCart, vatInvoiceData, needInvoice, false, currentTable, serviceFeeAmount, alreadySentCartItems, tableOrder?._id, payments, 0, (selectedVipCard ? selectedVipCard : selectedEmployee));
                }
            }
        } catch (error) {
            ErrorReportingService.error("Error in confirmPaymentAmount", { error }, "PaymentScreen");
            setProcessing(false); // Reset processing state on error
            if (Platform.OS === 'android') {
                ToastAndroid.showWithGravity(
                    'Error processing payment',
                    ToastAndroid.LONG,
                    ToastAndroid.CENTER
                );
            } else {
                alert('Error processing payment');
            }
        } finally {
            // Note: We don't set processing to false here because handlePayment will do that
            // The catch block will handle errors and set processing to false if needed
        }
    };

    // Memoize payment calculations to avoid unnecessary recalculations
    const paymentCalculations = useMemo(() => {
        const numericAmount = parseInt(amount) || 0;
        const splitPaymentSum = paymentsSum;
        const roundedOriginalAmount = originalAmount;
        const remainingAmount = payments?.length > 0 
            ? Math.max(roundedOriginalAmount - splitPaymentSum - numericAmount, 0) 
            : Math.max(roundedOriginalAmount - numericAmount, 0);
        const change = payments?.length > 0 
            ? Math.max(splitPaymentSum - roundedOriginalAmount, 0) 
            : Math.max(numericAmount - roundedOriginalAmount, 0);

        // Calculate amountToPay based on payment type
        const calculatedAmountToPay = paymentType === 'Készpénz'
            ? memoizedHungarianRound(remainingAmount)
            : remainingAmount;

        return {
            paidAmount: numericAmount,
            amountToPay: calculatedAmountToPay,
            changeToReturn: change
        };
    }, [amount, originalAmount, payments, paymentType, paymentsSum, memoizedHungarianRound]);

    // Apply the calculated values
    useEffect(() => {
        setPaidAmount(paymentCalculations.paidAmount);
        setAmountToPay(paymentCalculations.amountToPay);
        setChangeToReturn(paymentCalculations.changeToReturn);
    }, [paymentCalculations]);


    // Memoize payment type specific calculations
    const paymentTypeCalculations = useMemo(() => {
        if (!currentTable) return { originalAmount: 0 };

        const isCashPayment = paymentType === 'Készpénz' || paymentType === 'cash';
        const hasSelectedItems = selectedItemsToPay?.length > 0;
        const serviceFee = Number(serviceFeePercentage);

        if (isCashPayment) {
            if (hasSelectedItems) {
                if (!isNaN(serviceFee)) {
                    const calculatedAmount = ((selectedItemsSum * serviceFee) + selectedItemsSum);
                    return { originalAmount: memoizedHungarianRound(calculatedAmount) };
                } else {
                    // Keep original amount unchanged, only update amount in the other useEffect
                    return { originalAmount: originalAmount };
                }
            } else {
                const roundedAmount = memoizedHungarianRound(cartTotalWithServiceFee);
                return { originalAmount: roundedAmount };
            }
        } else {
            if (hasSelectedItems) {
                const serviceFee = Number(serviceFeePercentage);
                return { originalAmount: Math.round((selectedItemsSum * serviceFee) + selectedItemsSum) };
            } else {
                return { originalAmount: cartTotalWithServiceFee };
            }
        }
    }, [currentTable, paymentType, selectedItemsToPay, selectedItemsSum, serviceFeePercentage, cartTotalWithServiceFee, memoizedHungarianRound, originalAmount]);

    // Apply the calculated values when payment type changes
    useEffect(() => {
        if (currentTable && paymentTypeCalculations.originalAmount !== 0) {
            setOriginalAmount(paymentTypeCalculations.originalAmount);
        }
    }, [currentTable, paymentTypeCalculations]);
    const handleSwitchChange = (value) => {
        setNeedInvoice(value);
        if (value) {
            setModalVisible(true);
        }
    };
    const handleSwitchChangeNav = (value) => {
        setNeedNAV(value);
    };

    const handleModalDismiss = () => {
        setModalVisible(false);
        setNeedInvoice(false);
        setVatInvoiceData(false);
    };
    useEffect(() => {
        if(vatInvoiceData){
            setModalVisible(false)
        }
    },[vatInvoiceData]);

    // Memoize input handlers to prevent unnecessary re-renders
    const handleInput = useCallback((value) => {
        setAmount((prev) => ((prev === originalAmount || prev === 0) ? value : prev + value));
    }, [originalAmount]);

    const handleBackspace = useCallback(() => {
        setAmount((prev) => (prev.length > 1 ? prev.slice(0, -1) : ''));
    }, []);

    const handleClear = useCallback(() => {
        setAmount(0);
    }, []);
    // Memoize canBeClosed function to prevent unnecessary recalculations
    const canBeClosed = useMemo(() => {
        if(isSplitOrder){
            return ((paymentsSum >= (originalAmount - 4)));
        }
        return amountToPay <= 1;
    }, [isSplitOrder, paymentsSum, originalAmount, amountToPay]);

    // Memoize utility functions to prevent unnecessary re-renders
    const convertPaymentType = useCallback(() => {
        if (paymentType === "Készpénz") return "cash";
        if (paymentType === "Bankkártya") return "creditCard";
        if (paymentType === "Utalvány") return "voucher";
        if (paymentType === "VIP Számla") return "vipBalance";
        if (paymentType === "Személyzeti") return "employeeBalance";
        return "temporary";
    }, [paymentType]);

    const handleDeletePayment = useCallback((index) => {
        setPayments(prevPayments => {
            const updatedPayments = [...prevPayments];
            updatedPayments.splice(index, 1);
            return updatedPayments;
        });
    }, []);


    // Memoize addPayment function to prevent unnecessary re-renders
    const addPayment = useCallback(() => {
        if (!paymentType) {
            if (Platform.OS === "android") {
                return ToastAndroid.showWithGravity('Kérlek válassz fizetési módot', 5000, ToastAndroid.CENTER);
            } else {
                setProcessing(false);
                return alert('Kérlek válassz fizetési módot');
            }
        }

        const newPayment = {
            "paymentType": convertPaymentType(),
            "change": 1,
            "currency": "HUF",
        };

        if (paymentType === 'Készpénz' || paymentType === 'cash') {
            newPayment.amount = memoizedHungarianRound(Number(amount)) || 0;
        } else {
            newPayment.amount = Number(amount) || 0;
        }

        setPayments(prevPayments => [...prevPayments, newPayment]);
        setAmount(amountToPay);
        setPaymentType(false);
    }, [paymentType, amount, amountToPay, convertPaymentType, memoizedHungarianRound]);

    const confirmRemovePayments = (onConfirm) => {
        if (Platform.OS === 'web') {
            if (window.confirm("Már vannak kifizetett összegek. Szeretné eltávolítani őket és kikapcsolni a bontott rendelést?")) {
                onConfirm();
            }
        } else {
            Alert.alert(
                "Létező kifizetések",
                "Már vannak kifizetett összegek. Szeretné eltávolítani őket és kikapcsolni a bontott rendelést?",
                [
                    {
                        text: "Mégse",
                        style: "cancel"
                    },
                    {
                        text: "Igen",
                        onPress: onConfirm
                    }
                ],
                { cancelable: false }
            );
        }

    };
    function handleSplitorderToggle() {
        if (isSplitOrder && payments.length > 0) {
            confirmRemovePayments(() => {
                setPayments([]);
                setIsSplitOrder(false);
            });
        } else {
            setIsSplitOrder(!isSplitOrder);
            if (!isSplitOrder) {
                setAmount(originalAmount - paymentsSum);
            }
        }
    }

    // Memoize payment type change handler to prevent unnecessary re-renders
    const handlePaymentTypeChange = useCallback((type) => {
        setPaymentType(type);
        if (type === 'Utalvány' || type === 'voucher') {
            setPaymentType('Utalvány'); // voucher
            setVoucherModalVisible(true);
        }

        // Log payment type change
        ErrorReportingService.info("Payment type changed", { 
            type, 
            isSplitOrder, 
            hasAmount: amount > 0 
        }, "PaymentScreen");

        // Second click on a payment type adds it to payments IF split order
        if (paymentType === type && isSplitOrder && amount > 0) {
            if (paymentType !== "voucher" && paymentType !== "Utalvány") {
                addPayment();
            }
        } else {
            // Calculate new amount based on payment type
            let newAmount = amount;

            if (type === 'Készpénz') {
                if (isSplitOrder) {
                    if (paymentsSum < originalAmount && (amount === 0 || !amount)) {
                        newAmount = memoizedHungarianRound(originalAmount - paymentsSum);
                    } else {
                        newAmount = memoizedHungarianRound(amount);
                    }
                } else {
                    newAmount = memoizedHungarianRound(amount);
                }
            } else if (type === 'Bankkártya') {
                if (cartTotalWithServiceFee && currentTable) {
                    if (!isSplitOrder) {
                        newAmount = cartTotalWithServiceFee;
                    } else {
                        if (paymentsSum < originalAmount && (amount === 0 || !amount)) {
                            if (selectedItemsToPay?.length > 0) {
                                newAmount = Math.round((selectedItemsSum * serviceFeePercentage) + selectedItemsSum - paymentsSum);
                            } else {
                                newAmount = cartTotalWithServiceFee - paymentsSum;
                            }
                        } else {
                            if (selectedItemsToPay?.length > 0) {
                                newAmount = Math.round((selectedItemsSum * serviceFeePercentage) + selectedItemsSum - paymentsSum);
                            } else {
                                newAmount = cartTotalWithServiceFee - paymentsSum;
                            }
                        }
                    }
                } else {
                    if (!isSplitOrder) {
                        newAmount = originalAmount;
                    } else {
                        if (paymentsSum < originalAmount) {
                            newAmount = originalAmount - paymentsSum;
                        }
                    }
                }
            } else if (type === 'VIP Számla' || type === 'Személyzeti') {
                newAmount = originalAmount - paymentsSum;
            } else {
                newAmount = originalAmount - paymentsSum;

                if (!isSplitOrder) {
                    newAmount = originalAmount - paymentsSum;
                } else {
                    if (paymentsSum < originalAmount && (amount === 0 || !amount)) {
                        newAmount = originalAmount - paymentsSum;
                    }
                }
            }

            // Update amount state once with the calculated value
            setAmount(newAmount);
        }
    }, [
        paymentType, isSplitOrder, amount, paymentsSum, originalAmount, 
        cartTotalWithServiceFee, currentTable, selectedItemsToPay, 
        selectedItemsSum, serviceFeePercentage, memoizedHungarianRound, addPayment
    ]);

    useEffect(() => {
        return () => {
            setProcessing(false);
        }
    }, []);

        useEffect(() => {
            const callback = async (response) => {
                try {
                    if (response.responseData && response.responseData.status === "success" || response.responseData.status === "SUCCESS") {
                        setCart([]);
                        const tableOrder = orders?.find(
                            (order) =>
                                !order.storno &&
                                order.status !== "cancelled" &&
                                order.status !== "closed" &&
                                order.tableId === currentTable?._id
                        );
                        ErrorReportingService.info("Processing payment after successful response", { 
                            paymentType, 
                            tableOrderId: tableOrder?._id 
                        }, "PaymentScreen");

                        await handlePayment(paymentType, paidAmount, originalAmount, setProcessing, navigation, currentTable && serviceFeeAmount > 0 ? cartTotalWithServiceFee : cartTotal, setCart, cartItems, sendData, onClose, originalCartTotal, productsPrice, discountsForWholeCart, vatInvoiceData, needInvoice, true, currentTable, serviceFeeAmount, alreadySentCartItems, tableOrder?._id, payments)
                        setIsModalVisible(false)
                        return setProcessing(false);
                    } else {
                        setIsModalVisible(false)
                        setProcessing(false);
                        return navigation.navigate('FailedPayment', {...response.responseData, orderResponse: null});
                    }
                } catch (e) {
                    setIsModalVisible(false)
                    setProcessing(false);
                    ErrorReportingService.error("Failed to redirect", { error: e }, "PaymentScreen");
                }
            }

            const handleUrl = async ({url}) => {
                const response = VivaPaymentModule.handleCallbackUrl(url);
                return callback(response)
            };

            const subscription = Linking.addEventListener('url', handleUrl);

            return () => {
                subscription.remove();
            };
        }, []);
    const printReceiptAndTicket = (order) => {
        if (Platform.OS !== "web") {
            try {
                // Print receipt tickets
                new Promise(() => RawBTPrintTicket(order, order.items));

                if(cartItems.length > 0) new Promise(() => setTimeout(() => RawBTPrintKitchen(order, order.items), 500));

            } catch (e) {
                if (Platform.OS === "android") {
                    return ToastAndroid.showWithGravity('Nem sikerült a nyomtatás!', ToastAndroid.LONG, ToastAndroid.BOTTOM, 50);
                } else {
                    return alert('Nem sikerült a nyomtatás!');
                }
            }
        }
    }
    async function handleProforma() {
        try {
            setProcessing(true);
            const proforma = true;
            const tableOrder = orders?.find(
                (order) =>
                    !order.storno &&
                    order.status !== "cancelled" &&
                    order.status !== "closed" &&
                    order.tableId === currentTable?._id
            );
        const storeOrderResponse = await send_storeOrder(cartTotal, cartItems, "", 0, discountsForWholeCart, undefined, false,
            currentTable, serviceFeeAmount, true, alreadySentCartItems, tableOrder?._id, [],
            0, currentTable && serviceFeeAmount > 0 ? cartTotalWithServiceFee : cartTotal, (selectedVipCard ? selectedVipCard : selectedEmployee), undefined, selectedItemsToPay, tableOrder, proforma);
            printReceiptAndTicket(storeOrderResponse);

            if (setIsModalVisible !== undefined) {
                setIsModalVisible(false);
            }

            navigation.navigate('TablesScreen');
            removeEmployee();
            removeVipCard();

        } catch (error) {
            ErrorReportingService.error("Error handling proforma", { error }, "PaymentScreen");
            if (Platform.OS === 'android') {
                ToastAndroid.showWithGravity(
                    'Error handling proforma',
                    ToastAndroid.LONG,
                    ToastAndroid.CENTER
                );
            } else {
                alert('Error handling proforma');
            }
        } finally {
            setProcessing(false);
        }
    }

    return (
        <View style={[styles.container, { width: '100%' , height: "100%"}]}>
            <LinearGradient
                colors={['#EAE5EE', '#FEE6DE', '#EBE5F2']}
                style={{ flex: 1, width: '100%', height: '100%' , flexDirection:"row"}}
            >
                <Spinner
                    visible={processing}
                    textContent={t("processing...")}
                    textStyle={styles.spinnerTextStyle}
                />
                <View style={{flex: 2}}>
                    <View style={styles.tabsContainer}>
                    {paymentOptions.map(({ type, icon }) => (
                        <TouchableOpacity
                            key={type}
                            style={[styles.tab, paymentType === type && styles.activeTab]}
                            onPress={() => handlePaymentTypeChange(type)}
                        >
                            <Feather name={icon} size={18} color={paymentType === type ? '#f1b34b' : '#666'} />
                            <Text style={[styles.tabText, paymentType === type && styles.activeTabText]}>{translateDisplay(type)}</Text>
                        </TouchableOpacity>
                    ))}
                    {currentTable && (<TouchableOpacity
                        style={[styles.tab, showSplitItems && styles.activeTab]}
                        onPress={() => {setShowSplitItems(!showSplitItems)}}
                    >
                        <Feather name="users" size={18} color={showSplitItems ? '#f1b34b' : '#666'}/>
                        <Text style={[styles.tabText,showSplitItems && styles.activeTabText]}>{t('split_payment')}</Text>
                    </TouchableOpacity>)}
                        {currentTable && (<TouchableOpacity
                            style={[styles.tab, processing && {opacity: 0.7}]}
                            onPress={() => {
                               handleProforma()
                            }}
                            disabled={processing}
                        >
                            <Feather name="file-text" size={18} color={processing ? '#999' : '#666'}/>
                            <Text style={[styles.tabText, processing && {color: '#999'}]}>{t("proforma")}</Text>
                        </TouchableOpacity>)}
                    {/*<TouchableOpacity*/}
                    {/*    style={[styles.tab, isSplitOrder && styles.splitTab]}*/}
                    {/*    onPress={() => {*/}
                    {/*todo refactor it for bontott for now every order is "split" order so every order can be paid with multiple payment options*/}
                    {/*        handleSplitorderToggle()*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    <View style={{ flexDirection: 'row' }}>*/}
                    {/*        <View style={{ flexDirection: 'column', paddingRight: "4px" }}>*/}
                    {/*            <Feather name={"dollar-sign"} size={10} color={isSplitOrder ? 'white' : '#666'} />*/}
                    {/*            <Feather name={"credit-card"} size={10} color={isSplitOrder ? 'white' : '#666'} />*/}
                    {/*        </View>*/}
                    {/*        <Feather name={"shuffle"} size={19} color={isSplitOrder ? 'white' : '#666'} />*/}
                    {/*    </View>*/}
                    {/*    <Text style={[styles.tabText, isSplitOrder && styles.activeSplitTabText]}>Bontott</Text>*/}
                    {/*</TouchableOpacity>*/}
                </View>
                    {selectedVipCard && <View style={styles.vipCardContainer}>
                        {(selectedVipCard?.user?.name?.first.length < 1 && selectedVipCard?.user?.name?.last.length < 1)
                        || (!selectedVipCard?.user?.name && !selectedVipCard?.user?.name)
                            ?
                            <Text style={styles.vipCardText}>
                                {selectedVipCard.cardId}
                            </Text> : <Text style={styles.vipCardText}>
                                {selectedVipCard?.user?.name?.first} {selectedVipCard?.user?.name?.last}
                            </Text>}
                    </View>}
                    {selectedEmployee && <View style={styles.vipCardContainer}>
                        {(selectedEmployee?.name?.first.length < 1 && selectedEmployee?.name?.last.length < 1)
                        || (!selectedEmployee?.name && !selectedEmployee?.name)
                            ?
                            <Text style={styles.vipCardText}>
                                {t("unnamed")}
                            </Text> : <Text style={styles.vipCardText}>
                                {selectedEmployee?.name?.first} {selectedEmployee?.name?.last}
                            </Text>}
                    </View>}
                    {/* Payment Amount Input */}
                    <View style={styles.amountContainer}>
                        {/*{isSplitOrder && <TouchableOpacity onPress={addPayment} >*/}
                        {/*    <Feather name={"plus-square"} size={28} color="black"/>*/}
                        {/*</TouchableOpacity>}*/}

                        <TouchableOpacity onPress={handleClear} style={styles.clearButton}>
                            <Feather name="trash-2" size={28} color="black" />
                        </TouchableOpacity>
                        <TextInput
                            style={styles.amountInput}
                            value={formatAmountWithSpaces(amount)}
                            editable={true}
                            keyboardType="numeric"
                            placeholder={t("received_amount")}
                            placeholderTextColor={'#AAA'}
                            onFocus={() => {
                                setAmount('');
                                if (Platform.OS === 'android') {
                                    textInputRef.current?.blur(); // Prevents keyboard from opening on Android
                                }
                            }}
                            onChangeText={(text) => {
                                // Allow only numbers and one decimal point
                                const numericText = text.replace(/[^0-9.]/g, '');
                                setAmount(numericText);
                            }}
                            ref={textInputRef}
                            showSoftInputOnFocus={Platform.OS === 'android' ? false : true} // Prevents keyboard on Android
                        />
                        <TouchableOpacity onPress={handleBackspace} style={styles.backspaceButton}>
                            <Text style={styles.backspaceText}>⌫</Text>
                        </TouchableOpacity>
                    </View>
                    <View style={{flexDirection: "row"}}>
                        <View style={{flex: 2, paddingLeft: 10}}>
                            {/* Info Lines */}
                            {(cartItems.length > 0 || alreadySentCartItems?.length > 0) &&
                                <View style={styles.infoContainer}>
                                    <View style={styles.infoRow}>
                                        <Text style={styles.infoLabel}>{t("payable")}:</Text>
                                        <Text style={styles.infoValue}>{(originalAmount)}</Text>
                                    </View>
                                    <View style={[styles.infoRow, {
                                        color: Number(amountToPay) > 0 ? "red" : "#333",
                                        fontWeight: Number(amountToPay) > 0 ? "900" : "200"
                                    }]}>
                                        {(amountToPay >= 1 && !isSplitOrder) && (<><Text style={[styles.infoLabel, {
                                            color: Number(amountToPay) > 0 ? "red" : "#333",
                                            fontWeight: Number(amountToPay) > 0 ? "900" : "200"
                                        }]}>{t("amount_due")}:</Text>
                                            <Text style={[styles.infoValue, {
                                                color: Number(amountToPay) > 0 ? "red" : "#333",
                                                fontWeight: Number(amountToPay) > 0 ? "900" : "200"
                                            }]}>{priceFormatter(amountToPay)}</Text></>)}
                                        {(isSplitOrder) && (<><TouchableOpacity disabled
                                            onPress={() => toggleModal()}><Text style={[styles.infoLabel, {
                                            color: Math.round(paymentsSum) >= Math.round(originalAmount) ? "#3baa1a" : "#f1b34b",
                                            fontWeight: "900",
                                        }]}>{t("paid_so_far")}:</Text></TouchableOpacity>

                                            <TouchableOpacity disabled onPress={() => toggleModal()}><Text
                                                style={[styles.infoValue, {
                                                    color: Math.round(paymentsSum) >= Math.round(originalAmount) ? "#3baa1a" : "#f1b34b",
                                                    fontWeight: "900",
                                                }]}>{priceFormatter(paymentsSum)}</Text></TouchableOpacity></>)}
                                    </View>
                                    {Number(changeToReturn) > 1 && <View style={styles.infoRow}>
                                        {paymentType === 'Bankkártya' ?
                                            Number(changeToReturn) > 1 && (<>
                                                <Text
                                                    style={[styles.infoLabel, {fontWeight: Number(changeToReturn) > 1 ? "900" : "200"}]}>
                                                    {t("overpayment")}:
                                                </Text>
                                                <Text
                                                    style={[styles.infoValue, {fontWeight: Number(changeToReturn) > 1 ? "900" : "200"}]}>
                                                    {priceFormatter(changeToReturn)}
                                                </Text>
                                            </>)
                                            :
                                            changeToReturn > 4 && <>
                                                <Text
                                                    style={[styles.infoLabel, {fontWeight: Number(changeToReturn) > 4 ? "900" : "200"}]}>
                                                    {t("change")}:
                                                </Text>
                                                <Text
                                                    style={[styles.infoValue, {fontWeight: Number(changeToReturn) > 4 ? "900" : "200"}]}>
                                                    {priceFormatter(hungarianRoundNumbers(changeToReturn))}
                                                </Text>
                                            </>
                                        }


                                    </View>}

                                </View>}
                            {payments?.length > 0 && (
                                <View style={[styles.infoRow, {maxHeight: "300px", paddingLeft: 10, paddingRight: 5}]}>
                                        <FlatList
                                            data={payments}
                                            keyExtractor={(item, index) => index.toString()}
                                            style={{height:"300px"}}
                                            contentContainerStyle={{height: "300px"}}
                                            renderItem={({item, index}) => (
                                                <View style={styles.paymentItem}>
                                                    <Text
                                                        style={styles.paymentType}>{reverseConvertPaymentType(item.paymentType)}</Text>
                                                    <Text style={styles.paymentAmount}>{priceFormatter(item.amount)}</Text>
                                                    <TouchableOpacity onPress={() => handleDeletePayment(index)}>
                                                        <Feather name="trash-2" size={22} color="red"/>
                                                    </TouchableOpacity>
                                                </View>
                                            )}
                                        />
                            </View>)}
                        </View>
                        <View style={{flex: 3, paddingLeft: 10}}>{/*2*/}


                    <View style={styles.switchContainer}>
                        <Text style={styles.switchLabel}>{t("request_invoice")}</Text>
                        <Switch
                            vibrate={true}
                            thumbColor="#f1b34b"
                            trackColor={{ false: "#767577", true: "#ecbb69" }}
                            ios_backgroundColor="#3e3e3e"
                            value={needInvoice}
                            onValueChange={handleSwitchChange}
                        />
                        <VatModal visible={modalVisible} onSubmit={setVatInvoiceData} onDismiss={handleModalDismiss} />
                    </View>
                            <View style={styles.switchContainer}>
                        <Text style={styles.switchLabel}>Pénztárgép / Adóügyi nyomtatás</Text>
                        <Switch
                            vibrate={true}
                            thumbColor="#f1b34b"
                            trackColor={{ false: "#767577", true: "#ecbb69" }}
                            ios_backgroundColor="#3e3e3e"
                            value={needNAV}
                            onValueChange={handleSwitchChangeNav}
                        />
                        <VatModal visible={modalVisible} onSubmit={setVatInvoiceData} onDismiss={handleModalDismiss} />
                    </View>
                    <View style={styles.pinpadContainer}>
                        <PinPad onButtonClick={(value) => (value === 'X' ? addPayment() : handleInput(value.toString()))}/>
                    </View>
                    </View>
                    </View>
                    {/* Pinpad and Process Payment Button Container */}
                    {(cartItems.length > 0 || alreadySentCartItems?.length > 0) && <View style={styles.bottomContainer}>
                        {/* Pinpad */}


                        {/* Back and Process Payment Buttons */}
                        {(cartItems.length > 0 || alreadySentCartItems?.length > 0) && <View style={styles.buttonContainer}>
                            <TouchableOpacity style={styles.backButton} onPress={() => {
                                if(setIsModalVisible !== undefined) {
                                    setIsModalVisible(false)
                                }

                                navigation.navigate('HomeScreen');
                            }}>
                                <Feather name="arrow-left" size={24} color="#fff"/>
                            </TouchableOpacity>

                            {Math.round(paymentsSum) > Math.round(originalAmount) + 2 ? (
                                <>
                                    <TouchableOpacity 
                                        style={styles.borravaloButton} 
                                        onPress={() => {
                                            confirmPaymentAmount(true)
                                        }}
                                        disabled={processing}
                                    >
                                        <Text style={styles.processButtonText}>{t("tip")}</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity 
                                        style={[styles.noHorizontalPaddingButton, processing && {opacity: 0.7}]} 
                                        onPress={()=>{confirmPaymentAmount(false)}}
                                        disabled={processing}
                                    >
                                        <Text style={styles.processButtonText}>
                                            {t("change")}: {priceFormatter(hungarianRoundNumbers(isSplitOrder ? paymentsSum - originalAmount : changeToReturn))}
                                        </Text>
                                    </TouchableOpacity>
                                </>
                            ) : (
                                <TouchableOpacity 
                                    style={[
                                        styles.processButton, 
                                        {
                                            backgroundColor: ((!isSplitOrder && amountToPay > 1) || (isSplitOrder && paymentsSum < originalAmount -1))  ? "#f3a965" : "#ff8000",
                                            opacity: processing ? 0.7 : 1
                                        }
                                    ]} 
                                    disabled={processing || ((!isSplitOrder && amountToPay > 1) || (isSplitOrder && paymentsSum < originalAmount - 4))} 
                                    onPress={confirmPaymentAmount}
                                >
                                    {((!isSplitOrder && amountToPay > 1) || (isSplitOrder && paymentsSum < originalAmount - 4)) && 
                                        <Text style={styles.processButtonText}>
                                            {t("amount_due")}: {isSplitOrder ? priceFormatter(originalAmount - paymentsSum) : priceFormatter(amountToPay)}
                                        </Text>
                                    }
                                    {canBeClosed && 
                                        <Text style={styles.processButtonText}>
                                            {processing ? t("processing...") : t("store_recept")}
                                        </Text>
                                    }
                                </TouchableOpacity>
                            )}
                        </View>}
                    </View>}</View>
                {(currentTable && showSplitItems) &&

                    <View style={{flex: 1, backgroundColor: "rgba(255,157,96,0.09)", height: "100%"}}>
                        <View style={{paddingVertical: 22, paddingHorizontal: 10, backgroundColor: "#f1f1f1",  marginBottom: 5}}>
                            <Text style={{fontSize: 24, fontWeight: "bold", color: "#333", textAlign: "center"}}>{t("split_items_to_pay")}</Text>
                        </View>
                        <FlatList
                            data={combinedCartItems}
                            keyExtractor={(item, index) => `${item.id}-${index}`}
                            renderItem={renderItem}
                            contentContainerStyle={{paddingBottom: 20}}
                            style={{height: (height * 0.86)}}
                        />

                </View>}

                {(isSplitOrder && paymentsModal) && (
                    <Modal
                        visible={paymentsModal}
                        transparent={true}
                        animationType="fade"
                        onRequestClose={toggleModal}
                    >
                        <View style={styles.modalBackdrop}>
                            <View style={styles.modalContainer}>
                                <View style={styles.modalHeader}>
                                    <Text style={styles.modalTitle}>{t("paid_so_far")}</Text>
                                    <TouchableOpacity onPress={toggleModal}>
                                        <Feather name="x" size={24} color="#333" />
                                    </TouchableOpacity>
                                </View>

                                <FlatList
                                    data={payments}
                                    keyExtractor={(item, index) => index.toString()}
                                    style={{ maxHeight: height * 0.4 }}
                                    contentContainerStyle={{ flexGrow: 1 }}
                                    renderItem={({ item, index }) => (
                                        <View style={styles.paymentItem}>
                                            <Text style={styles.paymentType}>{reverseConvertPaymentType(item.paymentType)}</Text>
                                            <Text style={styles.paymentAmount}>{priceFormatter(item.amount)}</Text>
                                            <TouchableOpacity onPress={() => handleDeletePayment(index)}>
                                                <Feather name="trash-2" size={22} color="red" />
                                            </TouchableOpacity>
                                        </View>
                                    )}
                                />

                                <TouchableOpacity style={styles.closeButton} onPress={toggleModal}>
                                    <Text style={styles.closeButtonText}>{t("close")}</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </Modal>
                )}
                </LinearGradient>
            <VoucherModal
                visible={voucherModalVisible}
                onClose={() => setVoucherModalVisible(false)}
                onConfirm={validateAndAddVoucher}
                voucherCode={voucherCode}
                onVoucherCodeChange={setVoucherCode}
            />
            <HappyAlert
                visible={isAlertVisible}
                onConfirm={() => setIsAlertVisible(false)}
                onCancel={() => setIsAlertVisible(false)}
                type="error"
                message={t("voucher_code_is_invalid")}
            />

            {/* Add Teya Payment Dialog */}
            <TeyaPaymentDialog />

        </View>
        );
    };

        const styles = StyleSheet.create({
            container: {
                flex: 1,
                backgroundColor: '#fff',
                justifyContent: 'space-between',
                zIndex: 1000,
            },
            modalContainer: {
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(0,0,0,0.5)'
            },
            modalContent: {
                backgroundColor: 'white',
                padding: 20,
                borderRadius: 10,
                width: '80%',
                maxWidth: 400
            },
            input: {
                borderWidth: 1,
                borderColor: '#ccc',
                borderRadius: 5,
                padding: 10,
                marginBottom: 15
            },
            modalButtons: {
                flexDirection: 'row',
                justifyContent: 'flex-end'
            },
            modalButton: {
                marginLeft: 10,
                padding: 10
            },
            splitTab: {
                backgroundColor: '#f1b34b',
            },
            activeSplitTabText: {
                color: 'white',
            },
            itemContainer: {
                flexDirection: 'row',
                justifyContent: 'space-between',
                paddingHorizontal: 10,
                paddingVertical: 16,
                borderBottomWidth: 1,
                borderBottomColor: '#bbb',
                backgroundColor: '#ddd'
            },
            itemName: {
                fontSize: 16,
                fontWeight: 'bold'
            },
            unitPrice: {
                fontSize: 14,
                color: '#666'
            },
            extraText: {
                fontSize: 12,
                color: '#888'
            },
            itemPrice: {
                fontSize: 16,
                fontWeight: 'bold'
            },
            tabsContainer: {
                flexDirection: 'row',
                justifyContent: 'space-around',
                padding: 10,
                backgroundColor: '#f1f1f1',
            },
            tab: {
                flex: 1,
                padding: 12,
                alignItems: 'center',
            },
            activeTab: {
                borderBottomWidth: 2,
                borderBottomColor: '#f1b34b',
            },
            tabText: {
                fontSize: 16,
                color: '#666',
                marginTop: 4,
            },
            activeTabText: {
                color: '#f1b34b',
            },
            infoContainer: {
                marginTop: 20,
                padding: 10,
            },
            infoRow: {
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginVertical: 2,
            },
            infoLabel: {
                fontSize: 18,
                color: '#333',
                fontWeight: "500",
            },
            infoValue: {
                fontSize: 18,
                color: '#333',
                fontWeight: 'bold',
            },
            amountContainer: {
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 7,
                width: '100%',
            },
            backspaceButton: {
                marginRight: 10,
                marginLeft: 5,
            },
            backspaceText: {
                fontSize: 26,
                color: '#333',
            },
            amountInput: {
                marginTop: 10,
                fontSize: 30,
                textAlign: 'center',
                color: '#333',
                borderBottomWidth: 1,
                width: "75%"
            },
            clearButton: {
                marginLeft: 10,
                marginRight: 10,
            },
            clearText: {
                fontSize: 26,
                color: '#333',
            },
            bottomContainer: {
                flex: 1,
                justifyContent: 'flex-end',
                paddingBottom: 10,
            },
            pinpadContainer: {
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'center',
                width: "100%",
                height: height * 0.45,
                paddingHorizontal: 0,
                paddingBottom: 10,
                marginBottom: 5,
            },
            noHorizontalPaddingButton: {
                backgroundColor: '#F76E24',
                alignItems: 'center',
                paddingTop: 15,
                paddingBottom: 15,
                marginTop: 20,
                flex: 1,
                marginLeft: 0,
                borderBottomRightRadius: 5,
                borderTopRightRadius: 5,
                paddingLeft: 3,
                paddingRight: 3,
            },
            borravaloButton: {
                backgroundColor: '#f1b34b',
                alignItems: 'center',
                paddingTop: 15,
                paddingBottom: 15,
                marginTop: 20,
                paddingLeft: 10,
                paddingRight: 10,
                borderBottomRightRadius: 5,
                borderTopRightRadius: 5,
                width: 'auto',
            },
            buttonContainer: {
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingHorizontal: 16,
            },
            backButton: {
                backgroundColor: 'black',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 15,
                marginTop: 20,
                borderRadius: 5,
                borderBottomRightRadius: 0,
                borderTopRightRadius: 0
            },
            processButton: {
                backgroundColor: '#F76E24',
                alignItems: 'center',
                padding: 15,
                marginTop: 20,
                flex: 1,
                marginLeft: 0,
                borderBottomRightRadius: 5,
                borderTopRightRadius: 5
            },
            processButtonText: {
                fontSize: 20,
                color: '#fff',
                fontWeight: 'bold',
                paddingHorizontal: 5,
            },
            spinnerTextStyle: {
                color: '#FFF'
            },
            switchContainer: {
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingHorizontal: 16,
                paddingTop: 20,
                marginVertical: 10,
            },
            switchLabel: {
                fontSize: 16,
                color: '#000',
            },
            modalBackdrop: {
                flex: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                justifyContent: 'center',
                alignItems: 'center',
            },
            modalHeader: {
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottomWidth: 1,
                borderBottomColor: '#ddd',
                paddingBottom: 10,
                marginBottom: 10,
            },
            modalTitle: {
                fontSize: 20,
                fontWeight: 'bold',
                color: '#333',
            },
            paymentItem: {
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingVertical: 12,
                borderBottomWidth: 1,
                borderBottomColor: '#eee',
            },
            paymentType: {
                fontSize: 16,
                color: '#444',
                flex: 1,
            },
            paymentAmount: {
                fontSize: 16,
                fontWeight: 'bold',
                color: '#333',
                flex: 1,
                textAlign: 'right',
                marginRight: 15,
            },
            closeButton: {
                marginTop: 20,
                padding: 12,
                backgroundColor: '#f1b34b',
                borderRadius: 8,
                alignItems: 'center',
            },
            closeButtonText: {
                fontSize: 18,
                fontWeight: 'bold',
                color: '#fff',
            },
            vipCardContainer: {
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: '#f1b34b',
                padding: 10,
                borderRadius: 5,
            },
            vipCardText: {
                flex: 1,
                color: 'white',
                fontSize: 16,
                textAlign: 'center',
                fontWeight: 'bold',
            },
        });

export default PaymentScreen;
